/**
 * Main Template JavaScript
 * MSU2025 Template
 */

// Import Bootstrap JavaScript components
import { Collapse, Dropdown, Modal, Offcanvas, Popover, ScrollSpy, Tab, Toast, Tooltip } from 'bootstrap';

class TemplateMain {
    constructor() {
        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupTemplate();
            });
        } else {
            this.setupTemplate();
        }
    }

    setupTemplate() {
        this.initBootstrapComponents();
        this.initSmoothScrolling();
        this.initBackToTop();
        this.initMobileMenu();
        this.initImageLazyLoading();
        this.initFormValidation();
        this.initSearchToggle();
        this.initStickeyHeader();
    }

    /**
     * Initialize Bootstrap components
     */
    initBootstrapComponents() {
        // Initialize tooltips
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });

        // Initialize popovers
        const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'));
        popoverTriggerList.map(function (popoverTriggerEl) {
            return new Popover(popoverTriggerEl);
        });

        // Initialize toasts
        const toastElList = [].slice.call(document.querySelectorAll('.toast'));
        toastElList.map(function (toastEl) {
            return new Toast(toastEl);
        });
    }

    /**
     * Initialize smooth scrolling for anchor links
     */
    initSmoothScrolling() {
        const links = document.querySelectorAll('a[href^="#"]');

        links.forEach(link => {
            link.addEventListener('click', (e) => {
                const href = link.getAttribute('href');

                if (href === '#') return;

                const target = document.querySelector(href);
                if (target) {
                    e.preventDefault();

                    const headerOffset = 80;
                    const elementPosition = target.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            });
        });
    }

    /**
     * Initialize back to top button
     */
    initBackToTop() {
        // Create back to top button
        const backToTop = document.createElement('button');
        backToTop.innerHTML = '<i class="fas fa-arrow-up"></i>';
        backToTop.className = 'btn btn-primary back-to-top';
        backToTop.setAttribute('aria-label', 'Back to top');
        backToTop.style.cssText = `
            position: fixed;
            bottom: 2rem;
            right: 2rem;
            width: 3rem;
            height: 3rem;
            border-radius: 50%;
            display: none;
            z-index: 1030;
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        `;

        document.body.appendChild(backToTop);

        // Show/hide button based on scroll position
        window.addEventListener('scroll', () => {
            if (window.pageYOffset > 300) {
                backToTop.style.display = 'flex';
                backToTop.style.alignItems = 'center';
                backToTop.style.justifyContent = 'center';
            } else {
                backToTop.style.display = 'none';
            }
        });

        // Scroll to top on click
        backToTop.addEventListener('click', () => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    }

    /**
     * Initialize mobile menu functionality
     */
    initMobileMenu() {
        const navbarCollapse = document.getElementById('primary-nav');
        const navbarToggler = document.querySelector('.navbar-toggler');

        if (!navbarToggler || !navbarCollapse) return;

        // Disable Bootstrap's default data-bs-toggle behavior and handle manually
        navbarToggler.removeAttribute('data-bs-toggle');

        // Manual toggle handling
        navbarToggler.addEventListener('click', function(e) {
            e.preventDefault();

            if (navbarCollapse.classList.contains('show')) {
                // Close the menu
                navbarCollapse.classList.remove('show');
                this.classList.add('collapsed');
                this.setAttribute('aria-expanded', 'false');
            } else {
                // Open the menu
                navbarCollapse.classList.add('show');
                this.classList.remove('collapsed');
                this.setAttribute('aria-expanded', 'true');
            }
        });
    }

    /**
     * Initialize lazy loading for images
     */
    initImageLazyLoading() {
        if ('IntersectionObserver' in window) {
            const imageObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        img.src = img.dataset.src;
                        img.classList.remove('lazy');
                        img.classList.add('loaded');
                        imageObserver.unobserve(img);
                    }
                });
            });

            const lazyImages = document.querySelectorAll('img[data-src]');
            lazyImages.forEach(img => imageObserver.observe(img));
        }
    }

    /**
     * Initialize form validation
     */
    initFormValidation() {
        const forms = document.querySelectorAll('.needs-validation');

        forms.forEach(form => {
            form.addEventListener('submit', (event) => {
                if (!form.checkValidity()) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                form.classList.add('was-validated');
            });
        });
    }

    /**
     * Initialize search toggle functionality
     */
    initSearchToggle() {
        const searchToggle = document.querySelector('.search-toggle');
        const searchForm = document.querySelector('.search-form');

        if (searchToggle && searchForm) {
            searchToggle.addEventListener('click', (e) => {
                e.preventDefault();
                searchForm.classList.toggle('show');

                if (searchForm.classList.contains('show')) {
                    const searchInput = searchForm.querySelector('input[type="search"]');
                    if (searchInput) {
                        setTimeout(() => searchInput.focus(), 100);
                    }
                }
            });

            // Close search form when clicking outside
            document.addEventListener('click', (e) => {
                if (!searchToggle.contains(e.target) && !searchForm.contains(e.target)) {
                    searchForm.classList.remove('show');
                }
            });
        }
    }

    /**
     * Initialize sticky header
     */
    initStickeyHeader() {
        const header = document.querySelector('.site-header');

        if (header) {
            let lastScrollTop = 0;

            window.addEventListener('scroll', () => {
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

                if (scrollTop > 100) {
                    header.classList.add('scrolled');
                } else {
                    header.classList.remove('scrolled');
                }

                // Hide header on scroll down, show on scroll up
                if (scrollTop > lastScrollTop && scrollTop > 200) {
                    header.classList.add('hidden');
                } else {
                    header.classList.remove('hidden');
                }

                lastScrollTop = scrollTop;
            });
        }
    }
}

// Initialize template when DOM is ready
new TemplateMain();
